import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";

export const USERNAME_COOLDOWN_DAYS = 30;
const USERNAME_COOLDOWN_MS = USERNAME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

// Words we don't want users to claim - either site routes that would collide,
// generic "official"-sounding handles, or things that'd be confusing in
// a URL like /u/admin.
const RESERVED = new Set<string>([
  "about",
  "admin",
  "administrator",
  "api",
  "app",
  "apps",
  "auth",
  "cmd",
  "dashboard",
  "dev",
  "help",
  "home",
  "login",
  "logout",
  "me",
  "mod",
  "moderator",
  "niflets",
  "noreply",
  "official",
  "owner",
  "register",
  "root",
  "settings",
  "signin",
  "signup",
  "staff",
  "support",
  "system",
  "test",
  "u",
  "user",
  "users",
]);

const PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{2,19}$/;

export type ValidationResult =
  | { ok: true }
  | { ok: false; error: string };

export function validateUsername(raw: string): ValidationResult {
  const name = raw.trim();
  if (name.length === 0) return { ok: false, error: "Pick a username." };
  if (name.length < 3) return { ok: false, error: "At least 3 characters." };
  if (name.length > 20) return { ok: false, error: "20 characters max." };
  if (!PATTERN.test(name)) {
    return {
      ok: false,
      error: "Letters, numbers, underscores. Must start with a letter.",
    };
  }
  if (RESERVED.has(name.toLowerCase())) {
    return { ok: false, error: "That handle is reserved." };
  }
  return { ok: true };
}

export async function isUsernameAvailable(name: string): Promise<boolean> {
  if (!db) return false;
  const key = name.trim().toLowerCase();
  const snap = await getDoc(doc(db, "usernames", key));
  return !snap.exists();
}

type ClaimUserProfile = {
  displayName?: string | null;
  photoURL?: string | null;
  email?: string | null;
};

export class UsernameTakenError extends Error {
  constructor() {
    super("That username is already taken.");
    this.name = "UsernameTakenError";
  }
}

// Atomically reserve `usernames/{lower}` and write `users/{uid}` so two users
// can never end up with the same handle. The transaction re-reads the claim
// doc inside the critical section - if it exists by then, we abort.
export async function claimUsername(
  uid: string,
  name: string,
  profile: ClaimUserProfile
): Promise<void> {
  if (!db) throw new Error("Firestore is not initialized.");
  const v = validateUsername(name);
  if (!v.ok) throw new Error(v.error);

  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();
  const claimRef = doc(db, "usernames", lower);
  const userRef = doc(db, "users", uid);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(claimRef);
    if (existing.exists()) throw new UsernameTakenError();

    tx.set(claimRef, { uid });
    tx.set(userRef, {
      username: trimmed,
      usernameLower: lower,
      displayName: profile.displayName ?? null,
      photoURL: profile.photoURL ?? null,
      email: profile.email ?? null,
      createdAt: serverTimestamp(),
    });
  });
}

export class UsernameCooldownError extends Error {
  readonly availableAt: Date;

  constructor(availableAt: Date) {
    super(
      `Username can be changed again on ${availableAt.toLocaleDateString()}.`
    );
    this.name = "UsernameCooldownError";
    this.availableAt = availableAt;
  }
}

export function nextUsernameChangeAt(
  lastChangedAt: Timestamp | null | undefined
): Date | null {
  if (!lastChangedAt) return null;
  return new Date(lastChangedAt.toMillis() + USERNAME_COOLDOWN_MS);
}

export function canChangeUsernameNow(
  lastChangedAt: Timestamp | null | undefined
): boolean {
  const nextAt = nextUsernameChangeAt(lastChangedAt);
  if (!nextAt) return true;
  return Date.now() > nextAt.getTime();
}

// Atomic username transfer: free the old claim, take the new one, stamp the
// rate-limit timestamp on the user doc. Rules enforce the 30-day cooldown
// server-side; this client-side check just gives a nicer error before the
// roundtrip.
export async function changeUsername(
  uid: string,
  currentUsernameLower: string,
  newUsername: string,
  lastChangedAt: Timestamp | null | undefined
): Promise<void> {
  if (!db) throw new Error("Firestore is not initialized.");
  const v = validateUsername(newUsername);
  if (!v.ok) throw new Error(v.error);

  const trimmed = newUsername.trim();
  const newLower = trimmed.toLowerCase();
  if (newLower === currentUsernameLower) {
    throw new Error("That's already your username.");
  }
  if (!canChangeUsernameNow(lastChangedAt)) {
    throw new UsernameCooldownError(
      nextUsernameChangeAt(lastChangedAt) ?? new Date()
    );
  }

  const newClaimRef = doc(db, "usernames", newLower);
  const oldClaimRef = doc(db, "usernames", currentUsernameLower);
  const userRef = doc(db, "users", uid);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(newClaimRef);
    if (existing.exists()) throw new UsernameTakenError();

    tx.delete(oldClaimRef);
    tx.set(newClaimRef, { uid });
    tx.update(userRef, {
      username: trimmed,
      usernameLower: newLower,
      usernameChangedAt: serverTimestamp(),
    });
  });
}
